// Web-exclusive DateTimePicker component
// Uses react-native-ui-datepicker in a modal popup

import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  Pressable,
  TextInput,
} from 'react-native';
import DateTimePicker from 'react-native-ui-datepicker';
import { webColors, webBorderRadius, webShadows } from '../../constants/webStyles';

const WebDateTimePicker = ({
  value,
  onChange,
  mode = 'date',
  label,
  placeholder,
  minDate,
  maxDate,
  disabled = false,
  error,
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [tempDate, setTempDate] = useState(null);
  // Time input state (for mode === 'time')
  const [tempHours, setTempHours] = useState('');
  const [tempMinutes, setTempMinutes] = useState('');
  const [tempPeriod, setTempPeriod] = useState('AM');

  // Parse incoming value to Date object
  const parsedValue = useMemo(() => {
    if (!value) return null;
    if (value instanceof Date) return value;
    return new Date(value);
  }, [value]);

  // Format display value
  const displayValue = useMemo(() => {
    if (!parsedValue) return '';

    if (mode === 'time') {
      return parsedValue.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    }

    if (mode === 'datetime') {
      return parsedValue.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    }

    // Default: date mode
    return parsedValue.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
    });
  }, [parsedValue, mode]);

  // Get icon based on mode
  const icon = mode === 'time' ? '\u{1F550}' : '\u{1F4C5}';

  // Get placeholder based on mode
  const getDefaultPlaceholder = () => {
    switch (mode) {
      case 'time': return 'Select time';
      case 'datetime': return 'Select date & time';
      default: return 'Select date';
    }
  };

  const handleOpenModal = () => {
    if (disabled) return;
    if (mode === 'time') {
      // Parse existing time value into hours/minutes/period
      if (parsedValue) {
        const hours = parsedValue.getHours();
        const displayHours = hours % 12 || 12;
        setTempHours(String(displayHours).padStart(2, '0'));
        setTempMinutes(String(parsedValue.getMinutes()).padStart(2, '0'));
        setTempPeriod(hours >= 12 ? 'PM' : 'AM');
      } else {
        // Default to current time
        const now = new Date();
        const hours = now.getHours();
        const displayHours = hours % 12 || 12;
        setTempHours(String(displayHours).padStart(2, '0'));
        setTempMinutes(String(now.getMinutes()).padStart(2, '0'));
        setTempPeriod(hours >= 12 ? 'PM' : 'AM');
      }
    } else {
      setTempDate(parsedValue || new Date());
    }
    setModalVisible(true);
  };

  const handleConfirm = () => {
    if (mode === 'time') {
      // Convert 12-hour time to Date object with 24-hour format
      const hours = parseInt(tempHours) || 12;
      const minutes = parseInt(tempMinutes) || 0;
      const date = new Date();
      let hour24 = hours;
      if (tempPeriod === 'PM' && hours !== 12) hour24 = hours + 12;
      if (tempPeriod === 'AM' && hours === 12) hour24 = 0;
      date.setHours(hour24, minutes, 0, 0);
      onChange(date);
    } else {
      onChange(tempDate);
    }
    setModalVisible(false);
  };

  const handleCancel = () => {
    setTempDate(null);
    setTempHours('');
    setTempMinutes('');
    setTempPeriod('AM');
    setModalVisible(false);
  };

  const handleDateChange = ({ date }) => {
    setTempDate(date ? new Date(date) : null);
  };

  // Time input handlers with validation
  const handleHoursChange = (text) => {
    const num = text.replace(/[^0-9]/g, '');
    if (num === '' || (parseInt(num) >= 0 && parseInt(num) <= 12)) {
      setTempHours(num);
    }
  };

  const handleMinutesChange = (text) => {
    const num = text.replace(/[^0-9]/g, '');
    if (num === '' || (parseInt(num) >= 0 && parseInt(num) <= 59)) {
      setTempMinutes(num);
    }
  };

  // Custom styles for the date picker to match design system
  const pickerStyles = {
    calendarContainer: {
      backgroundColor: webColors.cardBackground,
    },
    monthSelector: {
      backgroundColor: webColors.backgroundAlt,
      borderRadius: webBorderRadius.small,
      paddingHorizontal: 12,
      paddingVertical: 8,
    },
    yearSelector: {
      backgroundColor: webColors.backgroundAlt,
      borderRadius: webBorderRadius.small,
      paddingHorizontal: 12,
      paddingVertical: 8,
    },
    selected: {
      backgroundColor: webColors.primary,
      borderRadius: webBorderRadius.small,
    },
    selected_label: {
      color: '#ffffff',
      fontWeight: '600',
    },
    today: {
      borderWidth: 2,
      borderColor: webColors.primary,
      borderRadius: webBorderRadius.small,
    },
    today_label: {
      color: webColors.primary,
      fontWeight: '600',
    },
    day: {
      borderRadius: webBorderRadius.small,
    },
    day_label: {
      color: webColors.textPrimary,
      fontSize: 14,
    },
    weekday: {
      color: webColors.textMuted,
      fontSize: 12,
      fontWeight: '500',
    },
    disabled: {
      opacity: 0.3,
    },
    disabled_label: {
      color: webColors.textMuted,
    },
    outside: {
      opacity: 0.4,
    },
    outside_label: {
      color: webColors.textMuted,
    },
    header: {
      backgroundColor: webColors.cardBackground,
      paddingVertical: 12,
    },
    button_prev: {
      backgroundColor: webColors.backgroundAlt,
      borderRadius: webBorderRadius.small,
      padding: 8,
    },
    button_next: {
      backgroundColor: webColors.backgroundAlt,
      borderRadius: webBorderRadius.small,
      padding: 8,
    },
    time_selector: {
      backgroundColor: webColors.backgroundAlt,
      borderRadius: webBorderRadius.small,
      paddingHorizontal: 12,
      paddingVertical: 8,
    },
    time_label: {
      color: webColors.textPrimary,
      fontSize: 16,
    },
    time_selected_indicator: {
      backgroundColor: webColors.primaryLight,
    },
  };

  return (
    <View style={styles.container}>
      {label && <Text style={styles.label}>{label}</Text>}

      {/* Input trigger */}
      <TouchableOpacity
        style={[
          styles.inputWrapper,
          disabled && styles.inputDisabled,
          error && styles.inputError,
        ]}
        onPress={handleOpenModal}
        activeOpacity={0.7}
        disabled={disabled}
      >
        <Text style={styles.inputIcon}>{icon}</Text>
        <Text
          style={[
            styles.inputText,
            !displayValue && styles.inputPlaceholder,
          ]}
        >
          {displayValue || placeholder || getDefaultPlaceholder()}
        </Text>
      </TouchableOpacity>

      {error && <Text style={styles.errorText}>{error}</Text>}

      {/* Modal */}
      <Modal
        visible={modalVisible}
        transparent
        animationType="fade"
        onRequestClose={handleCancel}
      >
        <Pressable style={styles.overlay} onPress={handleCancel}>
          <Pressable
            style={styles.modalContent}
            onPress={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>
                {mode === 'time' ? 'Select Time' :
                 mode === 'datetime' ? 'Select Date & Time' : 'Select Date'}
              </Text>
            </View>

            {/* Date/Time Picker */}
            <View style={styles.pickerContainer}>
              {mode === 'time' ? (
                <View style={styles.timePickerContainer}>
                  <View style={styles.timeInputRow}>
                    {/* Hour input */}
                    <TextInput
                      style={styles.timeInput}
                      value={tempHours}
                      onChangeText={handleHoursChange}
                      keyboardType="numeric"
                      maxLength={2}
                      placeholder="HH"
                      placeholderTextColor={webColors.textMuted}
                      selectTextOnFocus
                    />
                    <Text style={styles.timeSeparator}>:</Text>
                    {/* Minute input */}
                    <TextInput
                      style={styles.timeInput}
                      value={tempMinutes}
                      onChangeText={handleMinutesChange}
                      keyboardType="numeric"
                      maxLength={2}
                      placeholder="MM"
                      placeholderTextColor={webColors.textMuted}
                      selectTextOnFocus
                    />
                    {/* AM/PM toggle */}
                    <View style={styles.amPmToggle}>
                      <TouchableOpacity
                        style={[styles.amPmButton, tempPeriod === 'AM' && styles.amPmButtonActive]}
                        onPress={() => setTempPeriod('AM')}
                      >
                        <Text style={[styles.amPmText, tempPeriod === 'AM' && styles.amPmTextActive]}>AM</Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={[styles.amPmButton, tempPeriod === 'PM' && styles.amPmButtonActive]}
                        onPress={() => setTempPeriod('PM')}
                      >
                        <Text style={[styles.amPmText, tempPeriod === 'PM' && styles.amPmTextActive]}>PM</Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                </View>
              ) : (
                <DateTimePicker
                  mode="single"
                  date={tempDate}
                  onChange={handleDateChange}
                  timePicker={mode === 'datetime'}
                  minDate={minDate}
                  maxDate={maxDate}
                  styles={pickerStyles}
                  locale="en"
                  firstDayOfWeek={0}
                />
              )}
            </View>

            {/* Actions */}
            <View style={styles.modalActions}>
              <TouchableOpacity
                style={styles.cancelButton}
                onPress={handleCancel}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.confirmButton}
                onPress={handleConfirm}
              >
                <Text style={styles.confirmButtonText}>Confirm</Text>
              </TouchableOpacity>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    // Container takes full width of parent
  },
  label: {
    fontSize: 13,
    fontWeight: '600',
    color: webColors.textSecondary,
    marginBottom: 8,
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    paddingVertical: 14,
    paddingHorizontal: 14,
    cursor: 'pointer',
  },
  inputDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  inputError: {
    borderColor: webColors.error,
  },
  inputIcon: {
    fontSize: 16,
    marginRight: 10,
  },
  inputText: {
    flex: 1,
    fontSize: 14,
    fontWeight: '500',
    color: webColors.textPrimary,
  },
  inputPlaceholder: {
    color: webColors.textMuted,
    fontWeight: '400',
  },
  errorText: {
    fontSize: 12,
    color: webColors.error,
    marginTop: 4,
  },
  // Modal styles
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  modalContent: {
    backgroundColor: webColors.cardBackground,
    borderRadius: 20,
    width: '100%',
    maxWidth: 400,
    overflow: 'hidden',
    ...webShadows.modal,
  },
  modalHeader: {
    padding: 20,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: webColors.borderLight,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: webColors.textPrimary,
    textAlign: 'center',
  },
  pickerContainer: {
    padding: 16,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    padding: 16,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: webColors.borderLight,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 14,
    backgroundColor: webColors.backgroundAlt,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: webColors.textSecondary,
  },
  confirmButton: {
    flex: 1,
    paddingVertical: 14,
    backgroundColor: webColors.primary,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
  },
  confirmButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#ffffff',
  },
  // Time picker styles
  timePickerContainer: {
    alignItems: 'center',
    paddingVertical: 24,
  },
  timeInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  timeInput: {
    width: 70,
    height: 60,
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 1,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    textAlign: 'center',
    fontSize: 28,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  timeSeparator: {
    fontSize: 28,
    fontWeight: '600',
    color: webColors.textPrimary,
    marginHorizontal: 4,
  },
  amPmToggle: {
    flexDirection: 'row',
    marginLeft: 16,
    borderRadius: webBorderRadius.small,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: webColors.border,
  },
  amPmButton: {
    paddingHorizontal: 16,
    paddingVertical: 18,
    backgroundColor: webColors.backgroundAlt,
  },
  amPmButtonActive: {
    backgroundColor: webColors.primary,
  },
  amPmText: {
    fontSize: 14,
    fontWeight: '600',
    color: webColors.textSecondary,
  },
  amPmTextActive: {
    color: '#ffffff',
  },
});

export default WebDateTimePicker;
